import React, { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, X } from "lucide-react";
import { supabase } from "@/lib/supabase";

export function CustomerSelector({ onSelect, value, id }) {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("all"); // all, code, name, cod_rep, city
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userCodRep, setUserCodRep] = useState(null);
  const [canViewAllCustomers, setCanViewAllCustomers] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    checkUserRole();
  }, []);

  useEffect(() => {
    if (userRole !== null) {
      fetchCustomers();
    }
  }, [userRole, userCodRep, canViewAllCustomers]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const checkUserRole = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return;
      }

      const { data: userData, error } = await supabase
        .from('users')
        .select('role, cod_rep, ver_comisiones_globales')
        .eq('username', user.email)
        .maybeSingle();

      if (error) throw error;
      
      if (userData) {
        setUserRole(userData.role);
        setUserCodRep(userData.cod_rep);
        setCanViewAllCustomers(userData.ver_comisiones_globales || userData.role === 'admin');
      }
    } catch (error) {
      console.error('Error verificando rol:', error);
      setUserRole('cliente');
    }
  };

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('customers')
        .select('*')
        .order('name');

      // Si el usuario es vendedor (tenga o no ver_comisiones_globales), filtrar por su cod_rep
      if (userRole === 'vendedor' && userCodRep) {
        query = query.eq('cod_rep', userCodRep);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Filtro adicional de seguridad
      let filteredData = data || [];
      if (userRole === 'vendedor' && userCodRep) {
        filteredData = filteredData.filter(customer => customer.cod_rep === userCodRep);
      }

      setCustomers(filteredData);
      setFilteredCustomers(filteredData);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    
    if (!term) {
      setFilteredCustomers(customers);
      return;
    }

    const searchTermLower = term.toLowerCase();
    let filtered;

    switch (searchType) {
      case 'code':
        filtered = customers.filter(c => c.code?.toLowerCase().includes(searchTermLower));
        break;
      case 'name':
        filtered = customers.filter(c => c.name?.toLowerCase().includes(searchTermLower));
        break;
      case 'cod_rep':
        filtered = customers.filter(c => c.cod_rep?.toLowerCase().includes(searchTermLower));
        break;
      case 'city':
        filtered = customers.filter(c => c.city?.toLowerCase().includes(searchTermLower));
        break;
      default:
        filtered = customers.filter(c => 
          c.code?.toLowerCase().includes(searchTermLower) ||
          c.name?.toLowerCase().includes(searchTermLower) ||
          c.cod_rep?.toLowerCase().includes(searchTermLower) ||
          c.city?.toLowerCase().includes(searchTermLower)
        );
    }

    setFilteredCustomers(filtered);
  };

  const selectedCustomer = customers.find(c => c.id === value);

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <label htmlFor={`${id}-search`} className="sr-only">Buscar cliente</label>
          <Input
            id={`${id}-search`}
            name={`${id}-search`}
            type="text"
            placeholder="Buscar cliente..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            onFocus={() => setIsOpen(true)}
            className="pr-10"
          />
          <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        </div>
        <div>
          <label htmlFor={`${id}-search-type`} className="sr-only">Tipo de búsqueda</label>
          <select
            id={`${id}-search-type`}
            name={`${id}-search-type`}
            className="rounded-md border border-input bg-background px-3 py-2"
            value={searchType}
            onChange={(e) => setSearchType(e.target.value)}
          >
            <option value="all">Todo</option>
            <option value="code">Código</option>
            <option value="name">Nombre</option>
            <option value="city">Población</option>
          </select>
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-md shadow-lg border border-gray-200 max-h-96 overflow-auto">
          {loading ? (
            <div className="p-4 text-center text-gray-500">Cargando...</div>
          ) : filteredCustomers.length === 0 ? (
            <div className="p-4 text-center text-gray-500">No se encontraron clientes</div>
          ) : (
            <div className="py-1">
              {filteredCustomers.map((customer) => (
                <button
                  key={customer.id}
                  type="button"
                  className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                  onClick={() => {
                    onSelect(customer.id);
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                >
                  <div className="font-medium">{customer.name}</div>
                  <div className="text-sm text-gray-600">
                    {customer.code && <span className="mr-2">Código: {customer.code}</span>}
                    {customer.cod_rep && <span className="mr-2">Rep: {customer.cod_rep}</span>}
                  </div>
                  <div className="text-sm text-gray-500">
                    {customer.city && <span>{customer.city}</span>}
                    {customer.province && <span>, {customer.province}</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {selectedCustomer && (
        <div className="mt-2 p-2 bg-gray-50 rounded-md">
          <div className="flex justify-between items-start">
            <div>
              <div className="font-medium">{selectedCustomer.name}</div>
              <div className="text-sm text-gray-600">
                {selectedCustomer.code && <span className="mr-2">Código: {selectedCustomer.code}</span>}
                {selectedCustomer.cod_rep && <span className="mr-2">Rep: {selectedCustomer.cod_rep}</span>}
              </div>
              <div className="text-sm text-gray-500">
                {selectedCustomer.city && <span>{selectedCustomer.city}</span>}
                {selectedCustomer.province && <span>, {selectedCustomer.province}</span>}
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onSelect(null)}
              aria-label="Eliminar cliente seleccionado"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
} 